import React, { useSyncExternalStore } from 'react';
import useStyles from './styles';
import {
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Box,
  Stack,
} from '@mui/material';
import { useInvite } from '@elevator/api-hooks/organization/use-invite';
import { ROUTES } from 'config/Nav';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { reportError } from 'lib/errors';
import { PageHeader, VendorCard } from 'molecules';
import { Vendor } from 'types/organization';

const emptySubscribe = () => () => {};
const useHasMounted = () =>
  useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

/** Shape used by the loaded invite layout (API org + dev preview). */
type InviteLoadedOrganization = {
  organizationId?: string;
  name: string;
  description?: string | null;
  banner?: { sourceUrl?: string | null } | null;
};

/**
 * Development only: visit `/invite?preview=1` to render the success layout without a valid invite id.
 * Still runs `useInvite` with a dummy token (may 404 in network tab); UI ignores that response.
 * No `banner` — VendorCard shows its shared default image (no GraphQL).
 */
const DEV_PREVIEW_ORGANIZATION: InviteLoadedOrganization = {
  organizationId: 'dev-preview',
  name: 'Elevator Magazine',
  description:
    'Local preview (`?preview=1`, development only). Buttons show a toast and do not call the invite API.',
  banner: null,
};

function organizationToVendor(org: InviteLoadedOrganization): Vendor {
  return {
    organizationId: org.organizationId ?? '',
    name: org.name,
    banner: org.banner ?? null,
  } as Vendor;
}

const DEV_PREVIEW_INVITE_TOKEN = '__elevator_dev_preview__';

type InviteLoadedLayoutProps = {
  organization: InviteLoadedOrganization;
  classes: ReturnType<typeof useStyles>['classes'];
  mobile: boolean;
  onDeclineClick: () => void;
  onAcceptClick: () => void;
};

/**
 * Invite copy + actions around a display-only VendorCard when data is available (or dev preview).
 */
const InviteLoadedLayout = ({
  organization,
  classes,
  mobile,
  onDeclineClick,
  onAcceptClick,
}: InviteLoadedLayoutProps) => {
  const vendor = organizationToVendor(organization);

  return (
    <Stack className={classes.inviteContent} gap={3} alignItems="stretch">
      <Typography variant="body1" component="p">
        You have been invited to join{' '}
        <Box component="span" className={classes.bold}>
          {organization.name}
        </Box>
        !
      </Typography>

      <Box className={classes.vendorCardWrap}>
        <VendorCard vendor={vendor} interactive={false} imagePriority />
      </Box>

      {organization.description && (
        <Typography variant="body1" color="text.secondary" component="p" sx={{ m: 0 }}>
          {organization.description}
        </Typography>
      )}

      <Typography variant="body1" component="p">
        Would you like to accept?
      </Typography>

      <Stack
        direction={mobile ? 'column' : 'row'}
        gap={mobile ? 1 : 2}
        alignItems={mobile ? 'stretch' : 'center'}
        justifyContent="center"
        width={mobile ? '100%' : 'auto'}
        alignSelf="center"
      >
        <Button variant="outlined" onClick={onDeclineClick} fullWidth={mobile}>
          Decline
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onAcceptClick}
          fullWidth={mobile}
        >
          Accept
        </Button>
      </Stack>
    </Stack>
  );
};

/**
 * Invite Component
 *
 * Displays an organization invitation with accept/decline options.
 * Organization is shown with the same VendorCard as the submission flow (banner + name);
 * invite copy and actions sit outside the card. Page title via PageHeader.
 *
 * Features:
 * - PageHeader title “Organization Invite”
 * - Organization invitation display
 * - Accept/decline functionality
 * - Loading and error states
 * - Responsive design
 * - **Development:** `/invite?preview=1` renders the loaded layout with mock copy (no valid `id` required)
 *
 * Used in:
 * - /invite page
 *
 * @returns Invite component with organization details and action buttons
 */
const Invite = (): React.JSX.Element => {
  const { classes } = useStyles();

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const { enqueueSnackbar } = useSnackbar();

  const router = useRouter();
  const { id, preview } = router.query;

  /**
   * Avoid SSR / first-client-render mismatch: `router.isReady` is false on the server but often
   * true on the client’s first paint, which would change the tree before hydration completes.
   */
  const hasMounted = useHasMounted();

  const isDevPreview =
    process.env.NODE_ENV === 'development' && router.isReady && preview === '1';

  const inviteId = router.isReady && id && typeof id === 'string' ? id : '';
  const inviteTokenForHook = isDevPreview ? DEV_PREVIEW_INVITE_TOKEN : inviteId;

  const inviteReply = useInvite(inviteTokenForHook);
  const { inviteInfo, accept, decline, loading, error } = inviteReply;

  const layoutShell = (content: React.ReactNode) => (
    <Box className={classes.container} data-layout="invite">
      <Stack gap={3} width="100%">
        <PageHeader title="Organization Invite" divider />
        {content}
      </Stack>
    </Box>
  );

  const centeredState = (children: React.ReactNode) => (
    <Stack minHeight="50vh" alignItems="center" justifyContent="center">
      {children}
    </Stack>
  );

  // Keep server HTML and first client render identical (spinner), then read query + invite state.
  if (!hasMounted || !router.isReady) {
    return layoutShell(centeredState(<CircularProgress />));
  }

  if (isDevPreview) {
    const previewToast = () => {
      enqueueSnackbar('Preview mode — invite API is not called.', {
        variant: 'info',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });
    };
    return layoutShell(
      <InviteLoadedLayout
        organization={DEV_PREVIEW_ORGANIZATION}
        classes={classes}
        mobile={mobile}
        onDeclineClick={previewToast}
        onAcceptClick={previewToast}
      />,
    );
  }

  // Early return if no invite ID after router is ready
  if (!id || typeof id !== 'string') {
    return layoutShell(
      centeredState(
        <Typography variant="h6" color="textSecondary" align="center">
          Invalid invite link. Please check your invitation URL.
        </Typography>,
      ),
    );
  }

  const declineInvite = async () => {
    try {
      await decline();
      enqueueSnackbar('Invite declined.', {
        variant: 'success',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      router.push(ROUTES.HOME.to);
    } catch (err) {
      reportError(err, {
        metaData: { operation: 'declineInvite', inviteId: id },
      });
      enqueueSnackbar('Failed to decline invite. Please try again.', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const acceptInvite = async () => {
    try {
      await accept();
      enqueueSnackbar('Invite accepted.', {
        variant: 'success',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      router.push(ROUTES.HOME.to);
    } catch (err) {
      reportError(err, {
        metaData: { operation: 'acceptInvite', inviteId: id },
      });
      enqueueSnackbar('Failed to accept invite. Please try again.', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  // Loading state
  if (loading) {
    return layoutShell(centeredState(<CircularProgress />));
  }

  // Error state
  if (error || !inviteInfo) {
    return layoutShell(
      centeredState(
        <Typography variant="h6" color="textSecondary" align="center">
          {error
            ? 'Failed to load invite. Please try again later.'
            : 'Invite not found or has expired.'}
        </Typography>,
      ),
    );
  }

  // Ensure organization data exists
  if (!inviteInfo.organization) {
    return layoutShell(
      centeredState(
        <Typography variant="h6" color="textSecondary" align="center">
          Invalid invite data. Please contact the organization.
        </Typography>,
      ),
    );
  }

  const { organization } = inviteInfo;

  return layoutShell(
    <InviteLoadedLayout
      organization={organization}
      classes={classes}
      mobile={mobile}
      onDeclineClick={() => void declineInvite()}
      onAcceptClick={() => void acceptInvite()}
    />,
  );
};

export default Invite;
